import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate  } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import ScrollTop from "./components/scrollTop";
import "./components/css/ScrollTop.css";
import './components/css/Motion.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import UnsupportedBrowser from "./components/UnsupportedBrowser";
import BrowserCheck from "./components/BrowserCheck";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Gallery from "./pages/Gallery";
import Careers from "./pages/Careers";
import NoPage from "./pages/NoPage";
import Sitemap from "./pages/Sitemap";
import Explore from './pages/Explore';
import Trips from './pages/Trips';
import Loading from './components/Loader';
import { useLocation } from "react-router-dom";
import Maintain from './pages/Maintain';
import Quotation from './pages/Quotation';
import 'react-lazy-load-image-component/src/effects/blur.css';


function useReapplyTranslationOnRouteChange() {
  const location = useLocation();
  useEffect(() => {
    // try to force the injected combo to reapply translation
    const lang = localStorage.getItem("lang") || (navigator.language || "en").split("-")[0];
    const combo = document.querySelector(".goog-te-combo");
    if (combo) {
      combo.value = lang;
      combo.dispatchEvent(new Event("change"));
      // small retry
      setTimeout(() => {
        const c = document.querySelector(".goog-te-combo");
        if (c) {
          c.value = lang;
          c.dispatchEvent(new Event("change"));
        }
      }, 400);
    } else {
      // optional: reload (uncomment if you prefer reload on every route)
      window.location.reload();
    }
  }, [location]);
}


function App() {
  const [loading, setLoading] = useState(true);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  // Show splash for a few seconds
  useEffect(() => {
    setTimeout(() => setLoading(false), 3000);
  }, []);

  // Catch install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Decide whether to show install UI
  useEffect(() => {
    if (!deferredPrompt) return;

    const installed = localStorage.getItem("pwa-installed");
    const lastDismissed = localStorage.getItem("pwa-last-dismissed");

    if (installed) return;

    // Show prompt only after 3 days if previously dismissed
    if (lastDismissed) {
      const now = Date.now();
      const threeDays = 1000 * 60 * 60 * 24 * 3;
      if (now - parseInt(lastDismissed, 10) < threeDays) return;
    }

    // Wait 10 seconds before showing prompt
    const timeout = setTimeout(() => {
      setShowInstallPrompt(true);
    }, 10000);

    return () => clearTimeout(timeout);
  }, [deferredPrompt]);

  // Handle install click
  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();

    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem("pwa-installed", "true");
    } else {
      localStorage.setItem("pwa-last-dismissed", Date.now().toString());
    }

    setShowInstallPrompt(false);
    setDeferredPrompt(null);
  };

  // Optional: track actual install
  useEffect(() => {
    const onInstalled = () => {
      localStorage.setItem("pwa-installed", "true");
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  if (loading) return <Loading />;

  return (
    <>
      <Toaster position="bottom-right" toastOptions={{ duration: 3000 }} />

      {/* Custom PWA Install Prompt */}
      {showInstallPrompt && (
        <div
          style={{
            position: "fixed",
            bottom: "30px",
            left: "20px",
            zIndex: 999999999,
            backgroundColor: "#ffffff",
            borderRadius: "14px",
            padding: "18px 20px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
            border: "1px solid #e0e0e0",
            maxWidth: "320px",
            width: "90%",
            fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            animation: "slideIn 0.3s ease-in-out",
          }}
        >
          <h3 style={{ margin: "0 0 8px", fontSize: "17px", color: "#111" }}>
            📲 Install Joujo Safaris
          </h3>
          <p style={{ margin: "0 0 12px", fontSize: "14px", color: "#333" }}>
            Get a faster, offline experience. Add us to your home screen!
          </p>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
            <button
              onClick={handleInstallClick}
              style={{
                flex: 1,
                padding: "10px",
                backgroundColor: "#0d3b2e",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Install
            </button>
            <button
              onClick={() => {
                setShowInstallPrompt(false);
                localStorage.setItem("pwa-last-dismissed", Date.now().toString());
              }}
              style={{
                flex: 1,
                padding: "10px",
                backgroundColor: "#f2f2f2",
                color: "#333",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Main app */}
      <BrowserRouter>
        <Routes>
          <Route index element={<BrowserCheck><Home /></BrowserCheck>} />
          <Route path="/" element={<BrowserCheck><Home /></BrowserCheck>} />
          <Route path="/home" element={<BrowserCheck><Home /></BrowserCheck>} />
        <Route path="/unsupported" element={<UnsupportedBrowser />} />
          <Route path="/about" element={<BrowserCheck><About /></BrowserCheck>} />
          <Route path="/gallery" element={<BrowserCheck><Gallery /></BrowserCheck>} />
          <Route path="/explore/:name" element={<BrowserCheck><Explore /></BrowserCheck>} />
          <Route path="/careers" element={<BrowserCheck><Careers /></BrowserCheck>} />
          <Route path="/trips" element={<BrowserCheck><Trips /></BrowserCheck>} />
          <Route path="/contact" element={<BrowserCheck><Contact /></BrowserCheck>} />
          <Route path="/maintain" element={<BrowserCheck><Maintain /></BrowserCheck>} />
          <Route path="/sitemap" element={<BrowserCheck><Sitemap /></BrowserCheck>} />
          <Route path="/quotation" element={<BrowserCheck><Quotation /></BrowserCheck>} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<NoPage />} />
        </Routes>
      </BrowserRouter>


      <ScrollTop />
    </>
  );
  function BrowserCheck({ children }) {
  const navigate = useNavigate();

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();

    // Block Internet Explorer
    if (ua.includes("msie") || ua.includes("trident/")) {
      navigate("/unsupported");
    }

    // Block Phoenix Browser
    if (ua.includes("phoenix")) {
      navigate("/unsupported");
    }

    // Optionally block old Chrome/Firefox versions (<60 for example)
    const matchChrome = ua.match(/chrome\/(\d+)/);
    if (matchChrome && parseInt(matchChrome[1], 10) < 60) {
      navigate("/unsupported");
    }

    const matchFirefox = ua.match(/firefox\/(\d+)/);
    if (matchFirefox && parseInt(matchFirefox[1], 10) < 60) {
      navigate("/unsupported");
    }

  }, [navigate]);

  return children;
}

}

export default App;
